<template>
  <div class="login-page">
    <div class="login-main">
      <section class="login-hero">
        <div class="login-hero-top">
          <h1 class="login-hero-title">{{ brandData && brandData.login_hero_text ? brandData.login_hero_text : "Grow Your Leadership Skills" }}</h1>
          <div class="login-hero-tag">
            <span class="login-hero-tag-line"></span>
            <span class="login-hero-tag-text">Curated Excellence</span>
          </div>
        </div>
        <div class="login-hero-ticks" aria-hidden="true">
          <span class="tick tick-1"></span>
          <span class="tick tick-2"></span>
          <span class="tick tick-3"></span>
        </div>
        <div class="login-hero-graphic" aria-hidden="true">
          <div class="stair stair-1"></div>
          <div class="stair stair-2"></div>
          <div class="stair stair-3"></div>
          <div class="stair stair-4"></div>
          <div class="stair stair-5"></div>
        </div>
      </section>

      <section class="login-panel">
        <div class="login-panel-inner">
          <div class="login-heading">
            <img v-if="logoPath" :src="logoPath" alt="Brand Logo" class="login-panel-logo" />
            <h3>Set My Password</h3>
            <p class="login-subtext">
              Enter the email associated with your account. If it's registered in our system, we'll email you a link to create a new password.
            </p>
          </div>

          <div class="form-group">
            <label for="emailInput" class="field-label">Email Address</label>
            <input
              type="text"
              name="Email"
              v-model="Email"
              class="form-control"
              placeholder="Enter account email"
              required="required"
              id="emailInput"
            />
          </div>

          <div class="form-group button-div">
            <ButtonSubmit
              @click="resetPassword()"
              label="Set My Password"
              :disabled="isSubmitting"
              class="login-submit-btn"
              :style="{
                border: '1px solid transparent',
                boxShadow: 'none',
                padding: '14px 20px',
                fontSize: '16px',
                fontWeight: '700',
                borderRadius: '4px',
                width: '100%',
                backgroundColor: brandData && brandData.main_color2 ? brandData.main_color2 : '#b2c225',
                color: brandData && brandData.button_text_color ? brandData.button_text_color : '#0E5071'
              }"
            />
          </div>

          <div class="return-link">
            <router-link to="/login">Return to Sign In page</router-link>
          </div>

          <div class="btm-msg">
            <p>
              Unsure which email you used?<br />
              Look at any email received from us to find your email associated with your account.<br />
              If you've not received an email, contact our helpdesk on
              <a href="mailto:Help@TalentSage.com">Help@TalentSage.com</a>.
            </p>
          </div>
        </div>
      </section>
    </div>

    <div class="login-footer-wrap">
      <Footer :brand-data="brandData" />
    </div>
  </div>
</template>

<script>
import ButtonSubmit from "./ButtonSubmit.vue";
import Footer from "./FooterComponent.vue";
import CryptoJS from "crypto-js";
import api from "../api/api.js";
import {flashMessage} from "../functions.js";

export default {
  components: { ButtonSubmit, Footer },
  name: "ForgotPasswordScreen",
  props: {
    brandData: {
      type: Object,
      default: () => ({}),
    },
    logoPath: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    isLoggedIn: false,
    Email: "",
    isSubmitting: false
  }),
  mounted() {
    if (this.$route.query.email) {
      try {
        let decryptedEmail = this.decrypt(
          decodeURIComponent(this.$route.query.email),
          "seed"
        );
        this.Email = decryptedEmail;
      } catch (e) {
        console.error("Error decrypting email:", e);
      }
    }
  },
  methods: {
    encrypt(src, passphrase) {
      return CryptoJS.AES.encrypt(src, passphrase).toString();
    },
    decrypt(src, passphrase) {
      const bytes = CryptoJS.AES.decrypt(src, passphrase);
      return bytes.toString(CryptoJS.enc.Utf8);
    },
    resetPassword() {
      if (this.isSubmitting) {
        return null;
      }

      if (this.Email == "" || this.Email == null || this.Email == undefined) {
        flashMessage(this.$flashMessage, '#F47820', '#ffffff', "Please Enter a valid Email Address!")
        return null;
      }

      this.isSubmitting = true;
      this.$emit("passLoadingStatus", { loading: true, message: "Sending reset link..." });

      api.post("password-reset/request", { email: this.Email.trim() })
        .then((response) => {
          if (response.data.success) {
            this.$flashMessage.show({
              type: "success",
              title: "Password reset email sent!",
              message: "If an account exists with this email, you will receive password reset instructions.",
            });
            this.Email = "";
          } else {
            this.$flashMessage.show({
              type: "error",
              title: response.data.message || "Something went wrong",
              message: "",
            });
          }
        })
        .catch((e) => {
          this.$flashMessage.show({
            type: "error",
            title: "An error occurred. Please try again.",
            message: "",
          });
          console.error("Password reset request error:", e);
        })
        .finally(() => {
          this.isSubmitting = false;
          this.$emit("passLoadingStatus", { loading: false });
        });
    },
  },
};
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
}
.login-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
@media (min-width: 900px) {
  .login-main {
    flex-direction: row;
  }
}

.login-hero {
  display: none;
  position: relative;
  overflow: hidden;
  background: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"');
  color: #ffffff;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px;
  height: 100%;
  flex-shrink: 0;
}
@media (min-width: 900px) {
  .login-hero {
    display: flex;
    width: 50%;
  }
}

.login-hero-title {
  max-width: 420px;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ffffff;
}
.login-hero-tag {
  display: flex;
  align-items: center;
  gap: 16px;
}
.login-hero-tag-line {
  width: 48px;
  height: 1px;
  background: rgba(255, 255, 255, 0.4);
}
.login-hero-tag-text {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}
.login-hero-ticks {
  position: absolute;
  bottom: 48px;
  left: 48px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.tick {
  display: block;
  width: 4px;
  background: rgba(255, 255, 255, 0.4);
}
.tick-1 { height: 24px; }
.tick-2 { height: 48px; }
.tick-3 { height: 72px; }
.login-hero-graphic {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80%;
  height: 40%;
  background: rgba(255, 255, 255, 0.05);
  border-top-left-radius: 40px;
  overflow: hidden;
}
.stair {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  height: 16.6%;
}
.stair-1 { width: 50%; bottom: 0; }
.stair-2 { width: 42%; bottom: 16.6%; }
.stair-3 { width: 34%; bottom: 33.2%; }
.stair-4 { width: 26%; bottom: 49.8%; }
.stair-5 { width: 18%; bottom: 66.4%; }

.login-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 32px 24px;
  height: 100%;
  overflow-y: auto;
}
.login-panel-inner {
  width: 100%;
  max-width: 420px;
}
.login-heading {
  margin-bottom: 32px;
}
.login-panel-logo {
  display: block;
  max-height: 40px;
  width: auto;
  margin-bottom: 24px;
}
.login-heading h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #0b1c30;
  margin: 0 0 8px 0;
}
.login-subtext {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}

.form-group {
  position: relative;
  margin-bottom: 20px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #434750;
  margin-bottom: 6px;
}
.form-control {
  height: 56px;
  font-size: 1rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  box-sizing: border-box;
  width: 100%;
  border-radius: 4px;
  padding: 16.5px 14px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.form-control::placeholder {
  color: #94a3b8;
  opacity: 1;
}
.form-control:focus {
  outline: none;
  border: 1.5px solid v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#002955"');
  box-shadow: none;
}

.button-div {
  display: flex;
  justify-content: center;
  margin-top: 8px;
  margin-bottom: 20px;
}
.button-div :deep(.btn-submit) {
  border-radius: 4px !important;
  font-weight: 700 !important;
  width: 100% !important;
}

.return-link {
  text-align: center;
  margin-bottom: 24px;
}
.return-link a {
  color: v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#0E5071"');
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
}
.return-link a:hover {
  text-decoration: underline;
}

.btm-msg {
  text-align: center;
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.5;
}
.btm-msg a {
  color: v-bind('brandData && brandData.accent_color1 ? brandData.accent_color1 : "#F47820"');
  text-decoration: none;
}

.login-footer-wrap :deep(.footer-child) {
  padding: 24px 140px 24px 32px;
  margin: 0;
  height: auto;
  border-top: 1px solid #f1f5f9;
  align-items: center;
  font-size: 12px;
}
@media (max-width: 900px) {
  .login-footer-wrap :deep(.footer-child) {
    padding: 24px 32px;
    justify-content: center;
    text-align: center;
  }
}
.login-footer-wrap :deep(#footer-links) {
  align-items: center;
  gap: 4px;
}
.login-footer-wrap :deep(#footer-links a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
}
.login-footer-wrap :deep(#footer-links a:hover) {
  color: v-bind('brandData && brandData.main_color1 ? brandData.main_color1 : "#123f75"');
}
.login-footer-wrap :deep(#powered-by a) {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-size: 11px;
  color: #94a3b8;
  text-decoration: none;
}
</style>